// src/components/AvNavBar.tsx
import React, { useState, useEffect } from 'react'
import { Col, Nav, Navbar, NavDropdown, Row, OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  Database,
  GearWideConnected,
  PersonCircle,
  Grid3x3Gap,
  QuestionCircle,
} from 'react-bootstrap-icons'
import { Brain } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { activeBranding as branding } from '../config'
import { useFetchUserProfileQuery } from '../services/authApi'
import { useGetOrganizationBrandingPublicQuery } from '../services/orgsApi'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  selectActiveDatasetSubView,
  selectActiveInferenceSubView,
  selectActiveModelSubView,
  selectSelectedProjectId,
} from '../store/slices/projectSlice'
import { useGetProjectQuery } from '../services/projectsApi'
import { useAppSelector, useAnnotationStats } from '../store/hooks'
import { useFetchModelsQuery } from '../services/modelsApi'
import ConfirmDialog from './modals/ConfirmDialog'

interface NavButtonProps {
  onClick: () => void
  children: React.ReactNode
  active: boolean
  disabled?: boolean
}

const NavButton = React.forwardRef<HTMLDivElement, NavButtonProps>(
  ({ active, onClick, children, disabled }, ref) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
      <Nav.Item className="nav-dock-item" ref={ref}>
        <Nav.Link
          onClick={disabled ? undefined : onClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`d-flex align-items-center justify-content-center nav-dock-link ${
            active ? 'active' : ''
          } ${disabled ? 'disabled' : ''}`}
          style={{
            opacity: disabled ? 0.5 : 1,
            boxShadow: isHovered && !disabled ? '0 0 0 2px #604066' : undefined,
            transition: 'box-shadow 0.2s ease-in-out',
          }}
        >
          {children}
        </Nav.Link>
      </Nav.Item>
    )
  }
)

NavButton.displayName = 'NavButton'

const AvNavbar: React.FC = () => {
  const navigate = useNavigate()
  const { logoutAndNavigate } = useAuth()
  const location = useLocation()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const activeDatasetSubView = useAppSelector(selectActiveDatasetSubView)
  const activeInferenceSubView = useAppSelector(selectActiveInferenceSubView)
  const activeModelSubView = useAppSelector(selectActiveModelSubView)

  // State for the logo URL
  const [logoUrl, setLogoUrl] = useState<string>(branding.assets.logoSmall)

  // State for insufficient data dialog
  const [showInsufficientDataDialog, setShowInsufficientDataDialog] = useState(false)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: user } = useFetchUserProfileQuery()
  const pagesWithNavbar = ['overview', 'datasets', 'models', 'inference']

  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    undefined
  )

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  const { data: brandingBlob } = useGetOrganizationBrandingPublicQuery()

  // Update logo URL when organization branding changes
  useEffect(() => {
    if (brandingBlob) {
      const url = URL.createObjectURL(brandingBlob)
      setLogoUrl(url)

      // Cleanup function to revoke the object URL when component unmounts or brandingBlob changes
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setLogoUrl(branding.assets.logoSmall)
    }
  }, [brandingBlob])

  const handleLogout = () => {
    logoutAndNavigate()
  }

  const onViewChange = (view: string): void => {
    if (!selectedProjectId) {
      navigate('/projects/overview')
      return
    }

    if (view === 'inference' && modelsData?.models?.length === 0) {
      return
    }

    if (view === 'models' && annoStats?.statistics) {
      const hasEnoughData =
        annoStats.statistics.total_annotations >= 10 &&
        Object.keys(annoStats.statistics.annotations_per_class || {}).filter(
          (key) => key !== 'Unknown' && key !== 'null'
        ).length >= 1
      if (!hasEnoughData) {
        setShowInsufficientDataDialog(true)
        return
      }
    }

    // Set appropriate subview based on the view being navigated to
    let subview = ''
    if (view === 'datasets') {
      subview = activeDatasetSubView
    } else if (view === 'inference') {
      subview =
        activeInferenceSubView === 'Adaptive Active Labeling'
          ? 'adaptive-active-labeling'
          : activeInferenceSubView === 'Real Time'
          ? 'realtime'
          : activeInferenceSubView === 'Map (Beta)'
          ? 'map'
          : 'overview'
    } else if (view === 'models') {
      subview = activeModelSubView
    }
    navigate(`/${view}/${selectedProjectId}/${subview}`)
  }

  useEffect(() => {
    const currentPath: string = location.pathname

    if (currentPath.includes('/profile')) {
      navigate('/profile')
    } else if (currentPath.includes('/dashboard')) {
      navigate('/dashboard')
    } else if (!selectedProjectId && !localStorage.getItem('selectedProjectId')) {
      navigate('/projects')
    }
  }, [selectedProjectId, location.pathname, navigate])

  return (
    <>
      <Row className="g-0">
        <Col xs={12} className="p-0">
          <Navbar bg="dark" variant="dark" className="px-3 w-100">
            <Row className="w-100 align-items-center">
              <Col xs={2} className="d-flex justify-content-center">
                <Navbar.Brand className="p-0 cursor-pointer" onClick={() => navigate('/projects')}>
                  <img
                    src={logoUrl}
                    className="header-logo"
                    alt={`${branding.company.name} Logo`}
                    style={{ height: 'clamp(32px, 3.5vw, 48px)' }}
                  />
                </Navbar.Brand>
              </Col>

              <Col xs={8} className="d-flex justify-content-center">
                {pagesWithNavbar.includes(location.pathname.split('/')[1]) && (
                  <Nav className="d-flex align-items-center nav-dock">
                    <NavButton
                      onClick={() => onViewChange('overview')}
                      active={location.pathname.split('/')[1] === 'overview'}
                    >
                      <Grid3x3Gap size={14} /> <span className="nav-text">Overview</span>
                    </NavButton>

                    <NavButton
                      onClick={() => onViewChange('datasets')}
                      active={location.pathname.split('/')[1] === 'datasets'}
                    >
                      <Database size={14} /> <span className="nav-text">Datasets</span>
                    </NavButton>

                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="models-tooltip">
                          You must have at least 1 class and 10 annotations
                        </Tooltip>
                      }
                      trigger={
                        !annoStats?.statistics?.total_annotations ||
                        annoStats.statistics.total_annotations < 10 ||
                        Object.keys(annoStats?.statistics?.annotations_per_class || {}).length < 1
                          ? ['hover', 'focus']
                          : []
                      }
                    >
                      <NavButton
                        onClick={() => onViewChange('models')}
                        active={location.pathname.split('/')[1] === 'models'}
                      >
                        <Brain size={14} /> <span className="nav-text">Models</span>
                      </NavButton>
                    </OverlayTrigger>

                    <NavButton
                      onClick={() => onViewChange('inference')}
                      active={location.pathname.includes('/inference/')}
                    >
                      <GearWideConnected size={14} /> <span className="nav-text">Tools</span>
                    </NavButton>
                  </Nav>
                )}
              </Col>

              <Col xs={2} className="d-flex justify-content-center">
                <Nav className="ms-auto d-flex align-items-center">
                  <Nav.Item className="me-3">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip id="help-tooltip">Documentation</Tooltip>}
                    >
                      <Nav.Link
                        href="/docs/"
                        target="_blank"
                        className="d-flex align-items-center"
                        style={{ fontSize: '14px' }}
                      >
                        <QuestionCircle size={16} className="text-light" />
                      </Nav.Link>
                    </OverlayTrigger>
                  </Nav.Item>

                  <NavDropdown
                    title={
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '80px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            lineHeight: 0,
                          }}
                        >
                          <PersonCircle
                            size={28}
                            className="text-light"
                            style={{ margin: '0 auto' }}
                          />
                        </div>
                        <span className="text-light" style={{ fontSize: '14px', marginTop: '2px' }}>
                          {user?.first_name || 'My Profile'}
                        </span>
                      </div>
                    }
                    id="basic-nav-dropdown"
                    className="dropdown-dark no-caret"
                    menuVariant="dark"
                    align="end"
                  >
                    <NavDropdown.Item onClick={() => navigate('/profile')}>
                      Profile
                    </NavDropdown.Item>
                    {user?.username === 'admin' && (
                      <NavDropdown.Item onClick={() => navigate('/dashboard')}>
                        User Management
                      </NavDropdown.Item>
                    )}
                    <NavDropdown.Divider />
                    <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                  </NavDropdown>
                </Nav>
              </Col>
            </Row>
          </Navbar>
        </Col>
      </Row>
      <style>
        {`
        .nav-dock-item.dropdown {
          height: 80%;
          position: relative;
          transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
        }
        .nav-dock-item.dropdown > .dropdown-toggle {
          padding: 0.4rem 0.8rem;
          height: 80%;
          display: flex;
          align-items: center;
          color: var(--bs-nav-link-color);
          transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
          background-color: #3e4145;
        }
        .nav-dock-item.dropdown > .dropdown-toggle:hover,
        .nav-dock-item.dropdown > .dropdown-toggle:focus {
          color: var(--bs-nav-link-hover-color);
          background-color: #3e4145;
        }
        .nav-dock-item.dropdown > .dropdown-toggle::after {
          display: none;
        }
        .nav-dock-item.dropdown.active > .dropdown-toggle,
        .nav-dock-item.dropdown.show > .dropdown-toggle {
          color: var(--bs-nav-link-active-color) !important;
          background-color: #2b2b2b !important;
          box-shadow: 0 0 0 1px #604066, 0 2px 6px rgba(0, 0, 0, 0.2);
        }
        .nav-dock-item.dropdown .dropdown-menu {
          background-color: #2a2a2a;
          border: 1px solid rgba(255, 255, 255, 0.15);
          margin-top: 0;
          padding: 0.4rem 0;
        }
        .nav-dock-item.dropdown .dropdown-item {
          color: var(--bs-nav-link-color);
          padding: 0.4rem 0.8rem;
          background-color: transparent;
          transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
          font-size: 14px;
        }
        .nav-dock-item.dropdown .dropdown-item:hover,
        .nav-dock-item.dropdown .dropdown-item:focus {
          color: var(--bs-nav-link-hover-color);
          background-color: #2b2b2b;
        }
        .nav-dock-item.dropdown .dropdown-item.active,
        .nav-dock-item.dropdown .dropdown-item:active {
          color: var(--bs-nav-link-active-color);
          background-color: #2b2b2b;
        }
        .nav-dock-link {
          color: var(--bs-nav-link-color) !important;
          background-color: #3e4145;
          padding: 0.4rem 0.8rem !important;
          font-size: 14px;
        }
        .nav-dock-link:hover,
        .nav-dock-link:focus {
          color: var(--bs-nav-link-hover-color) !important;
          background-color: #800085;
        }
        .nav-dock-link.active {
          color: var(--bs-nav-link-active-color) !important;
          background-color: #2b2b2b;
          box-shadow: 0 0 0 1px #604066, 0 2px 6px rgba(0, 0, 0, 0.2);
        }
        .nav-dock-link svg {
          opacity: 0.75;
          margin-right: 4px;
        }
        .nav-dock-link.active svg {
          opacity: 1;
        }
        `}
      </style>

      <ConfirmDialog
        show={showInsufficientDataDialog}
        onHide={() => setShowInsufficientDataDialog(false)}
        onConfirmRight={() => setShowInsufficientDataDialog(false)}
        title="Insufficient Data for Models"
        message={
          <div>
            <p>You need more data before you can access the Models section.</p>
            <p>
              <strong>OBB and BBox Requirements:</strong>
            </p>
            <ul>
              <li>
                At least <strong>10 annotations</strong>
              </li>
              <li>
                At least <strong>1 class</strong> (excluding 'Unknown' or 'null')
              </li>
            </ul>
            <p>
              <strong>Classificaiton Requirements:</strong>
            </p>
            <ul>
              <li>
                At least <strong>10 annotations</strong>
              </li>
              <li>
                At least <strong>2 classes</strong>
              </li>
            </ul>
            <p>Please add more annotations to your dataset first.</p>
          </div>
        }
        confirmRightText="OK"
        variant="warning"
      />
    </>
  )
}

export default AvNavbar
